import type { RequestOnboardingLinkInput } from '@houzy/schemas/partner-cockpit/onboarding';
import {
    invitationInfoSchema,
    partnerCockpitUserSchema,
    type ChangePartnerCockpitPasswordInput,
    type InvitationInfo,
    type LoginPartnerCockpitUserInput,
    type PartnerCockpitImpersonation,
    type PartnerCockpitMembership,
    type PartnerCockpitMembershipStatus,
    type PartnerCockpitRole,
    type PartnerCockpitUser,
    type RequestPartnerCockpitEmailChangeInput,
    type SetPartnerCockpitPasswordInput,
    type UpdatePartnerCockpitProfileInput,
} from '@houzy/schemas/partner-cockpit/user-management';
import {
    queryOptions,
    useMutation,
    useQuery,
    useQueryClient,
    type QueryClient,
    type UseQueryOptions,
} from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';

import { settingsMutationKey } from '../features/settings/api/settingsApi';
import type { SupportedLanguage } from '../i18n';
import {
    ApiContractError,
    ApiError,
    apiRequest,
    invalidatePartnerCockpitCsrfToken,
    retryQueryUnlessClientError,
} from './api';
import { PARTNER_COCKPIT_ROUTE_PREFIX, partnerCockpitApiPath } from './config';
import { mixpanelClient } from './mixpanelClient';
import { forgetOtherStoredUser } from './mixpanelContext';
import type { RollbarPerson } from './rollbar';
import { clearCockpitSessionHint, hasCockpitSessionHint, rememberCockpitSession } from './sessionHint';
import { trackEvent, type LoginMethod } from './trackingEvents';
import { rollbarClient } from './useRollbar';

// The user/membership shapes and the login request are defined once in @houzy/schemas
// and shared with the backend. Re-exported so existing importers keep their import path.
export type {
    LoginPartnerCockpitUserInput,
    PartnerCockpitImpersonation,
    PartnerCockpitMembership,
    PartnerCockpitMembershipStatus,
    PartnerCockpitRole,
    PartnerCockpitUser,
};

export function selectImpersonation(user: PartnerCockpitUser | undefined): PartnerCockpitImpersonation | undefined {
    return user?.impersonation ?? undefined;
}

export type LoginRequest = LoginPartnerCockpitUserInput;

export const currentUserQuery = queryOptions({
    queryKey: [`${PARTNER_COCKPIT_ROUTE_PREFIX}-user`, 'me'],
    queryFn: async ({ signal }): Promise<PartnerCockpitUser> => {
        const user = await apiRequest(partnerCockpitApiPath('user/'), {
            signal,
            responseSchema: partnerCockpitUserSchema,
        });
        // Every route that needs a session goes through this query, so it is the one place that learns the
        // answer first-hand.
        rememberCockpitSession();
        return user;
    },
    retry: retryQueryUnlessClientError,
    staleTime: 1000 * 60,
    // The auth cookie is httpOnly, so the client can't see when the session ends and we need to re-validate when the user
    // returns to the tab so a session terminated elsewhere is caught without a manual action.
    refetchOnWindowFocus: true,
});

export function useCurrentUser(): PartnerCockpitUser | undefined {
    return useQuery(currentUserQuery).data;
}

// TODO: This is not yet decided on.
export function selectActiveMembership(user: PartnerCockpitUser | undefined): PartnerCockpitMembership | undefined {
    return user?.memberships.find((membership) => membership.status === 'Active');
}

export function selectHasActivePartner(user: PartnerCockpitUser | undefined): boolean {
    return selectActiveMembership(user) !== undefined;
}

export function selectIsPendingValidation(user: PartnerCockpitUser | undefined): boolean {
    return user?.pendingValidation === true;
}

export function selectRollbarPerson(user: PartnerCockpitUser | undefined): RollbarPerson | undefined {
    if (!user) {
        return undefined;
    }
    return {
        id: user.uuid,
        email: user.email,
        partnerUuid: selectActiveMembership(user)?.partnerUuid,
        impersonatorEmail: selectImpersonation(user)?.impersonatorEmail,
    };
}

// Sent before the identity hook catches up with the new session, so a previous user still stored on this browser is
// cleared here first or the login would count as theirs.
export function trackLogin(method: LoginMethod, user: PartnerCockpitUser): void {
    forgetOtherStoredUser(mixpanelClient, user.uuid);
    trackEvent('Logged In', { method, partnerUuid: selectActiveMembership(user)?.partnerUuid });
}

// Adopts `user` as the current session. Whatever is still cached belongs to whoever was signed in before, and a
// session that dies server-side rather than through logout leaves all of it behind, so the cache goes first. The
// CSRF token is bound to the session's user too, so it cannot outlive the switch either.
export function startPartnerCockpitSession(queryClient: QueryClient, user: PartnerCockpitUser): void {
    // Adopted without a fetch, so the query above never runs for it.
    rememberCockpitSession();
    invalidatePartnerCockpitCsrfToken();
    queryClient.clear();
    queryClient.setQueryData(currentUserQuery.queryKey, user);
}

// A teardown navigates to /login before clearing the cache (see `useEndLocalSession`), so for that navigation
// the cached user outlives the session it describes and nothing may read it as "signed in".
let sessionTeardownInProgress = false;

export function startSessionTeardown(): boolean {
    if (sessionTeardownInProgress) {
        return false;
    }
    sessionTeardownInProgress = true;
    // Covers a logout whose request never reached the server, which leaves both cookies in place.
    clearCockpitSessionHint();
    return true;
}

export function finishSessionTeardown(): void {
    sessionTeardownInProgress = false;
}

// Runs the rest of a teardown once the navigation settles, whatever it settles as. The flag gates every guard
// that asks whether anyone is signed in and the previous session's data stays resident until the clear, so a
// navigation that rejects — or throws before a `.finally` could even be attached — must not strand either.
export function completeSessionTeardown(queryClient: QueryClient, navigateToLogin: () => Promise<unknown>): void {
    void (async (): Promise<void> => {
        try {
            // Clearing before this settles would cancel the very request that reported the expiry, leaving the
            // awaiting route guard with a `CancelledError` instead of the 401 it knows how to redirect on.
            await navigateToLogin();
        } catch (error) {
            rollbarClient.error(
                'Partner cockpit sign-out navigation failed',
                error instanceof Error ? error : new Error(String(error)),
            );
        } finally {
            queryClient.clear();
            finishSessionTeardown();
        }
    })();
}

// Whether a visitor can be sent into the cockpit instead of an auth page. Membership is part of it: a craftsman
// consuming an onboarding link is signed in without one, and every page behind the app shell loads
// `validationQueryOptions`, which 403s — so redirecting them would swap a usable form for the error screen.
export async function hasUsableCockpitSession(queryClient: QueryClient): Promise<boolean> {
    if (sessionTeardownInProgress) {
        return false;
    }
    const status = queryClient.getQueryState(currentUserQuery.queryKey)?.status;
    // A query that succeeded and later failed keeps its last `data`, so the status is what says it is gone.
    if (status === 'error') {
        return false;
    }
    // Nothing cached and no hint is nearly every visit to these pages: answer it without a request.
    if (status !== 'success' && !hasCockpitSessionHint()) {
        return false;
    }
    try {
        return selectHasActivePartner(await queryClient.ensureQueryData(currentUserQuery));
    } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            clearCockpitSessionHint();
        }
        // Offline, a 500 or a payload the deployed schema no longer matches says nothing about the session, so
        // the hint stays. Unlike a protected route this one renders fine without an answer, so nothing rethrows.
        return false;
    }
}

// `/user/` answers TOKEN_MISSING for an anonymous visitor as well as for an expired session, so the error alone
// cannot tell a session that ended from one that never existed. Only the former should hijack a navigation.
export function hasCachedCockpitUser(queryClient: QueryClient): boolean {
    return queryClient.getQueryState(currentUserQuery.queryKey)?.data !== undefined;
}

export function useLoginMutation(): ReturnType<
    typeof useMutation<PartnerCockpitUser, ApiError | ApiContractError, LoginRequest>
> {
    const queryClient = useQueryClient();
    return useMutation<PartnerCockpitUser, ApiError | ApiContractError, LoginRequest>({
        mutationFn: (body: LoginRequest): Promise<PartnerCockpitUser> =>
            apiRequest(partnerCockpitApiPath('auth/login'), {
                method: 'POST',
                body,
                responseSchema: partnerCockpitUserSchema,
            }),
        onSuccess: (user: PartnerCockpitUser) => {
            startPartnerCockpitSession(queryClient, user);
            trackLogin('password', user);
        },
    });
}

// Ends the local session and sends the user to /login. The cache is cleared only once the redirect has swapped
// the authenticated tree out: clearing it while that tree is still mounted cancels every query those components
// have in flight and makes the rest refetch without a session.
export function tearDownLocalSession(queryClient: QueryClient, navigateToLogin: () => Promise<unknown>): boolean {
    invalidatePartnerCockpitCsrfToken();
    if (!startSessionTeardown()) {
        return false;
    }
    completeSessionTeardown(queryClient, navigateToLogin);
    return true;
}

function useEndLocalSession(): () => void {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    return (): void => {
        tearDownLocalSession(queryClient, () => navigate({ to: '/login' }));
    };
}

function requestLogout(): Promise<void> {
    return apiRequest(partnerCockpitApiPath('auth/logout'), { method: 'POST' });
}

function reportLogoutFailure(error: unknown): void {
    rollbarClient.error(
        'Partner cockpit logout request failed',
        error instanceof Error ? error : new Error(String(error)),
    );
}

export function endServerSession(): void {
    requestLogout().catch(reportLogoutFailure).finally(invalidatePartnerCockpitCsrfToken);
}

export function useLogoutMutation(): ReturnType<typeof useMutation<void, ApiError>> {
    const endLocalSession = useEndLocalSession();
    return useMutation<void, ApiError>({
        mutationFn: requestLogout,
        // Mutation-level rather than passed to `mutate`: React Query skips the call-site callbacks once the
        // observer has no listeners, and a concurrent 401 elsewhere can unmount the whole authenticated tree
        // while this request is still in flight — which would leave the previous user's data in the cache.
        onSettled: () => {
            endLocalSession();
        },
        // The local session goes either way, so a failure only means the server session may linger.
        onError: reportLogoutFailure,
    });
}

export function useStopImpersonationMutation(): ReturnType<typeof useMutation<void, ApiError>> {
    const endLocalSession = useEndLocalSession();
    return useMutation<void, ApiError>({
        mutationFn: (): Promise<void> => apiRequest(partnerCockpitApiPath('auth/impersonate/stop'), { method: 'POST' }),
        onSettled: () => {
            endLocalSession();
        },
        // As with logout: the local session goes either way, so a failure only means the server session may
        // linger. Its own label, because the generic `mutationCache.onError` one cannot be filtered on.
        onError: (error) => {
            rollbarClient.error('Partner cockpit stop impersonation request failed', error);
        },
    });
}

export function useRequestPasswordResetMutation(): ReturnType<typeof useMutation<void, ApiError, { email: string }>> {
    return useMutation<void, ApiError, { email: string }>({
        mutationFn: (body: { email: string }): Promise<void> =>
            apiRequest(partnerCockpitApiPath('auth/password/request-reset'), {
                method: 'POST',
                body,
            }),
    });
}

export function useSetPasswordMutation(): ReturnType<
    typeof useMutation<PartnerCockpitUser, ApiError | ApiContractError, { otp: string; password: string }>
> {
    const queryClient = useQueryClient();
    return useMutation<PartnerCockpitUser, ApiError | ApiContractError, { otp: string; password: string }>({
        mutationFn: ({ otp, password }): Promise<PartnerCockpitUser> =>
            apiRequest(partnerCockpitApiPath(`auth/password/reset/${otp}`), {
                method: 'POST',
                body: { password },
                responseSchema: partnerCockpitUserSchema,
            }),
        onSuccess: (user: PartnerCockpitUser) => {
            startPartnerCockpitSession(queryClient, user);
        },
    });
}

type InvitationInfoQueryKey = readonly [`${typeof PARTNER_COCKPIT_ROUTE_PREFIX}-invitation`, string];

export function invitationInfoQueryOptions(
    otp: string,
): UseQueryOptions<InvitationInfo, Error, InvitationInfo, InvitationInfoQueryKey> {
    return queryOptions({
        queryKey: [`${PARTNER_COCKPIT_ROUTE_PREFIX}-invitation`, otp] as const,
        queryFn: ({ signal }): Promise<InvitationInfo> =>
            apiRequest(partnerCockpitApiPath(`auth/invitation/${otp}`), {
                signal,
                responseSchema: invitationInfoSchema,
            }),
        retry: false,
        staleTime: Infinity,
    });
}

type AcceptInvitationVariables = { otp: string; password?: string; tosAccepted?: boolean };

export function useAcceptInvitationMutation(): ReturnType<
    typeof useMutation<PartnerCockpitUser, ApiError | ApiContractError, AcceptInvitationVariables>
> {
    const queryClient = useQueryClient();
    return useMutation<PartnerCockpitUser, ApiError | ApiContractError, AcceptInvitationVariables>({
        mutationFn: ({ otp, password, tosAccepted }): Promise<PartnerCockpitUser> =>
            apiRequest(partnerCockpitApiPath(`auth/invitation/consume/${otp}`), {
                method: 'POST',
                body: { password, tosAccepted },
                responseSchema: partnerCockpitUserSchema,
            }),
        onSuccess: (user: PartnerCockpitUser) => {
            startPartnerCockpitSession(queryClient, user);
        },
    });
}

export function useAcceptTermsMutation(): ReturnType<
    typeof useMutation<PartnerCockpitUser, ApiError | ApiContractError, void>
> {
    const queryClient = useQueryClient();
    return useMutation<PartnerCockpitUser, ApiError | ApiContractError, void>({
        mutationFn: (): Promise<PartnerCockpitUser> =>
            apiRequest(partnerCockpitApiPath('user/accept-terms'), {
                method: 'POST',
                responseSchema: partnerCockpitUserSchema,
            }),
        onSuccess: (user: PartnerCockpitUser) => {
            queryClient.setQueryData(currentUserQuery.queryKey, user);
        },
    });
}

export function useRequestMagicLinkMutation(): ReturnType<typeof useMutation<void, ApiError, { email: string }>> {
    return useMutation<void, ApiError, { email: string }>({
        mutationFn: (body: { email: string }): Promise<void> =>
            apiRequest(partnerCockpitApiPath('auth/magic-link/request'), {
                method: 'POST',
                body,
            }),
    });
}

export function useRequestOnboardingLinkMutation(): ReturnType<
    typeof useMutation<void, ApiError, RequestOnboardingLinkInput>
> {
    return useMutation<void, ApiError, RequestOnboardingLinkInput>({
        mutationFn: (body: RequestOnboardingLinkInput): Promise<void> =>
            apiRequest(partnerCockpitApiPath('onboarding/request-link'), {
                method: 'POST',
                body,
            }),
    });
}

export function useUpdateProfileMutation(): ReturnType<
    typeof useMutation<PartnerCockpitUser, ApiError | ApiContractError, UpdatePartnerCockpitProfileInput>
> {
    const queryClient = useQueryClient();
    return useMutation<PartnerCockpitUser, ApiError | ApiContractError, UpdatePartnerCockpitProfileInput>({
        mutationKey: [...settingsMutationKey, 'profile'],
        mutationFn: (body: UpdatePartnerCockpitProfileInput): Promise<PartnerCockpitUser> =>
            apiRequest(partnerCockpitApiPath('user/'), {
                method: 'PATCH',
                body,
                responseSchema: partnerCockpitUserSchema,
            }),
        onSuccess: (user: PartnerCockpitUser) => {
            queryClient.setQueryData(currentUserQuery.queryKey, user);
        },
    });
}

export function useChangePasswordMutation(): ReturnType<
    typeof useMutation<void, ApiError, ChangePartnerCockpitPasswordInput>
> {
    return useMutation<void, ApiError, ChangePartnerCockpitPasswordInput>({
        mutationFn: (body: ChangePartnerCockpitPasswordInput): Promise<void> =>
            apiRequest(partnerCockpitApiPath('auth/password/change'), {
                method: 'POST',
                body,
            }),
        onSuccess: () => {
            // The backend re-issues this device's auth cookie and other devices are signed out. We need to drop the
            // cached CSRF token so the next mutation fetches a fresh one bound to the refreshed session.
            invalidatePartnerCockpitCsrfToken();
        },
    });
}

export function useSetInitialPasswordMutation(): ReturnType<
    typeof useMutation<void, ApiError, SetPartnerCockpitPasswordInput>
> {
    const queryClient = useQueryClient();
    return useMutation<void, ApiError, SetPartnerCockpitPasswordInput>({
        mutationFn: (body: SetPartnerCockpitPasswordInput): Promise<void> =>
            apiRequest(partnerCockpitApiPath('auth/password/set'), {
                method: 'POST',
                body,
            }),
        onSuccess: async () => {
            invalidatePartnerCockpitCsrfToken();
            await queryClient.invalidateQueries({ queryKey: currentUserQuery.queryKey });
        },
        onError: (error: ApiError) => {
            if (error.errorCode === 'PASSWORD_ALREADY_SET') {
                void queryClient.invalidateQueries({ queryKey: currentUserQuery.queryKey });
            }
        },
    });
}

export function useRequestEmailChangeMutation(): ReturnType<
    typeof useMutation<void, ApiError, RequestPartnerCockpitEmailChangeInput>
> {
    return useMutation<void, ApiError, RequestPartnerCockpitEmailChangeInput>({
        mutationFn: (body: RequestPartnerCockpitEmailChangeInput): Promise<void> =>
            apiRequest(partnerCockpitApiPath('auth/email/change-request'), {
                method: 'POST',
                body,
            }),
    });
}

export function useUpdateLanguageMutation(): ReturnType<typeof useMutation<void, ApiError, SupportedLanguage>> {
    const queryClient = useQueryClient();
    return useMutation<void, ApiError, SupportedLanguage>({
        mutationFn: (language: SupportedLanguage): Promise<void> =>
            apiRequest(partnerCockpitApiPath('user/language'), {
                method: 'POST',
                body: { language },
            }),
        onSuccess: (_data: void, language: SupportedLanguage) => {
            const cached = queryClient.getQueryData<PartnerCockpitUser>(currentUserQuery.queryKey);
            if (cached) {
                queryClient.setQueryData<PartnerCockpitUser>(currentUserQuery.queryKey, { ...cached, language });
            }
        },
    });
}
